import { useMemo, useState } from 'react';

// * MUI
import {
  Alert,
  AlertTitle,
  alpha,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// * MUI Icons
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// * Utils
import {
  getTypeColor,
  getTypeIcon,
  getGroupedOptions,
} from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import {
  actionValidation,
  filterOptionGroupsForAdd,
  mapUnitsToDataStreams,
  validateTree,
} from '../../utils/energyTreeFunctions';

// * Redux
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';

// * API
import { usePostEnergyTreeNodesMutation } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';

const AddNodeTab = ({
  clickedNode,
  energyGroup,
  tree,
  decoratedUnits,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const account = useSelector(getSelectedAccount);

  const [postEnergyTreeNodes] = usePostEnergyTreeNodesMutation();

  const [searchText, setSearchText] = useState('');
  const [residualSource, setResidualSource] = useState(''); // default
  const [makeResidualNode, setMakeResidualNode] = useState(false); // shown when Virtual
  const [formState, setFormState] = useState({
    name: '',
    type: '',
    dataStreams: [],
    energyGroupId: energyGroup?.energyGroupId || null,
    parentId: clickedNode?.energyTreeNodeId || null,
    options: {
      specialNodeType: null,
      power: null,
      percentage: null,
    },
  });
  const groupedOptions = getGroupedOptions();

  const { invalidNodeIds, errors } = useMemo(() => validateTree(tree), [tree]);

  console.table({ invalidNodeIds, errors });

  const filteredOptionGroups = useMemo(() => {
    return filterOptionGroupsForAdd(clickedNode, tree, groupedOptions);
  }, [clickedNode, groupedOptions, tree]); // re-compute when user changes the parent/context

  const validation = useMemo(() => {
    return actionValidation({
      action: 'add',
      targetType: formState.type,
      parent: clickedNode,
      tree: tree,
    });
  }, [formState.type, clickedNode, tree]);

  const handleAddNode = async () => {
    const payload = { ...formState };
    if (!makeResidualNode) {
      payload.options = null;
    }

    try {
      const createdNode = await postEnergyTreeNodes({
        energyTreeNode: payload,
        accountId: account.AccountId,
      }).unwrap();
      dispatch(showSnackbar({ message: 'Node tilføjet', severity: 'success' }));
      handleClose();
      return createdNode; // ✅ <-- Add this line
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i tilføjelse af node',
          severity: 'error',
        }),
      );
      return null; // Optional: return null on error
    }
  };

  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>
          {clickedNode?.name || 'Node Details'}
        </Typography>
        <Box
          sx={{
            backgroundColor: getTypeColor(clickedNode?.type),
            borderRadius: '25px',
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px solid divider`,
          }}
        >
          <i
            className={`fa-solid ${getTypeIcon(clickedNode?.type)}`}
            style={{ color: 'white' }}
          ></i>
        </Box>
      </Box>

      {validation.isValid === false && (
        <Alert
          severity="info"
          sx={(theme) => ({
            mt: 1,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            borderLeft: `6px solid ${theme.palette.info.main}`,
          })}
        >
          <AlertTitle>Kan ikke tilføje node</AlertTitle>
          {validation.invalidReason || 'Ugyldig konfiguration'}
        </Alert>
      )}

      {validation.isValid && (
        <>
          <TextField
            label="Navn"
            value={formState.name}
            helperText="Indtast navnet på den nye node."
            onChange={(e) =>
              setFormState({ ...formState, name: e.target.value })
            }
            fullWidth
            variant="filled"
          />

          <TextField
            label="Node Type"
            select
            value={formState.type}
            onChange={(e) =>
              setFormState({ ...formState, type: e.target.value })
            }
            fullWidth
            helperText={
              validation.isValid
                ? 'Vælg en type for noden.'
                : validation.invalidReason || 'Understøttes ikke'
            }
            error={!validation.isValid}
            variant="filled"
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 500 },
                },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <i className={`fa-solid ${getTypeIcon(formState.type)}`} />
                  </InputAdornment>
                ),
              },
            }}
          >
            <MenuItem value="" disabled>
              <Typography color="text.secondary">Vælg type</Typography>
            </MenuItem>

            {Object.entries(filteredOptionGroups).map(
              ([groupLabel, options]) => {
                if (!options.length) return null;

                return [
                  <ListSubheader key={`header-${groupLabel}`}>
                    {groupLabel}
                  </ListSubheader>,
                  ...options.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      <Box display="flex" width="100%">
                        <Box
                          sx={{
                            backgroundColor: getTypeColor(value),
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            mr: 1,
                            height: 28,
                            width: 28,
                            justifyContent: 'center',
                            fontSize: '14px',
                          }}
                        >
                          <i className={`fa-solid ${getTypeIcon(value)}`} />
                        </Box>
                        <Typography>{label}</Typography>
                      </Box>
                    </MenuItem>
                  )),
                ];
              },
            )}
          </TextField>

          {!makeResidualNode && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={decoratedUnits}
              getOptionLabel={(option) => `${option.UnitId} - ${option.Name}`}
              value={formState.dataStreams.map((ds) => {
                return decoratedUnits.find((unit) => unit.UnitId === ds.unitId);
              })}
              inputValue={searchText}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== 'reset') setSearchText(newInputValue);
              }}
              getOptionDisabled={(option) =>
                !!option.usedInNode || !!option.notSupportedReason
              }
              onClose={() => setSearchText('')}
              onChange={(e, newValue) => {
                setFormState({
                  ...formState,
                  dataStreams: mapUnitsToDataStreams(newValue),
                });
              }}
              filterSelectedOptions
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography>
                      ({option.UnitId}) {option.Name}
                    </Typography>
                    {option.usedInNode && (
                      <Typography variant="caption" sx={{ color: 'gray' }}>
                        Anvendt for: {option.usedInNode}
                      </Typography>
                    )}
                    {option.notSupportedReason && (
                      <Typography variant="caption" sx={{ color: 'red' }}>
                        {option.notSupportedReason}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Units"
                  variant="filled"
                  helperText="Vælg de Units som skal associeres med denne node. Disse enheder vil blive brugt i oversigten til at vise energiforbrugsdata, hvis det er tilgængeligt."
                  placeholder="Vælg units"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    sx={{ borderRadius: 1 }}
                    key={option.UnitId}
                    label={`${option.Name}`}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          )}

          <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={makeResidualNode}
                    onChange={() => {
                      setMakeResidualNode((prev) => {
                        const next = !prev;

                        if (!next) {
                          setFormState((fs) => ({
                            ...fs,
                            options: {
                              specialNodeType: null,
                              power: null,
                              percentage: null,
                            },
                          }));
                        }

                        if (next) {
                          setFormState((fs) => ({
                            ...fs,
                            dataStreams: [],
                            options: {
                              specialNodeType: 'RestResidual',
                              power: null,
                              percentage: null,
                            },
                          }));
                        }

                        return next;
                      });
                    }}
                    sx={{ mx: 1 }}
                  />
                }
                label="Opret som residual node"
              />
              <FormHelperText>
                Ved at oprette som en residual node, er det ikke muligt at
                associere datastrømme/units med denne node. I stedet kan man ved
                oprettelse af en residual node vælge om noden skal reflektere
                det resterende forbrug, eller om hvorvidt man anslår en fast
                effekt for noden, eller en procentvis andel.
              </FormHelperText>
            </FormGroup>

            {makeResidualNode && (
              <>
                <TextField
                  select
                  label="Rolle"
                  sx={{ mt: 2 }}
                  value={residualSource}
                  helperText="Vælg hvilken rolle denne node har. Dette påvirker hvordan det repræsenterede data fremgår."
                  onChange={(e) => {
                    const next = e.target.value;
                    setResidualSource(next);

                    setFormState((fs) => {
                      const updated = { ...fs };
                      updated.options.specialNodeType = next;
                      if (next !== 'FlatResidual') {
                        updated.options.power = null;
                      }
                      if (next !== 'PercentageResidual') {
                        updated.options.percentage = null;
                      }
                      return updated;
                    });
                  }}
                  fullWidth
                  variant="filled"
                >
                  <MenuItem value="RestResidual">Restforbrug</MenuItem>
                  <MenuItem value="FlatResidual">Fast effekt (kW)</MenuItem>
                  <MenuItem value="PercentageResidual">
                    Procentandel (%)
                  </MenuItem>
                </TextField>

                {residualSource === 'FlatResidual' && (
                  <TextField
                    label="Fast effekt (kW)"
                    type="number"
                    inputProps={{ min: 0, step: 'any' }}
                    value={formState?.options?.power || ''}
                    onChange={(e) => {
                      setFormState((fs) => ({
                        ...fs,
                        options: {
                          ...fs.options,
                          power:
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                        },
                      }));
                    }}
                    variant="filled"
                    fullWidth
                    helperText="Angiv en fast residualeffekt i kW."
                  />
                )}

                {residualSource === 'PercentageResidual' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom>Procentandel (%)</Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <TextField
                        type="number"
                        variant="filled"
                        hiddenLabel
                        value={(
                          (formState?.options?.percentage ?? 0) * 100
                        ).toFixed(1)} // limit to 2 decimals
                        onChange={(e) => {
                          const pct = parseFloat(e.target.value);
                          const clamped = isNaN(pct)
                            ? 0
                            : Math.min(Math.max(pct, 0), 100);
                          setFormState((fs) => ({
                            ...fs,
                            options: {
                              ...fs.options,
                              percentage: clamped / 100,
                            },
                          }));
                        }}
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 0.1,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PercentRoundedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Slider
                        value={formState?.options?.percentage * 100}
                        onChange={(_, newVal) =>
                          setFormState((fs) => ({
                            ...fs,
                            options: {
                              ...fs.options,
                              percentage: newVal / 100,
                            },
                          }))
                        }
                        min={0.1}
                        max={100}
                        step={0.1}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Card>
        </>
      )}

      <Box display="flex" justifyContent="end" mt={2}>
        <Button
          onClick={handleAddNode}
          disableElevation
          disableRipple
          disabled={!validation.isValid}
          sx={{
            borderRadius: 2,
            backgroundColor: 'hsl(0, 0%, 20%)',
            color: 'hsl(0, 0%, 90%)',
            '&:hover': {
              backgroundColor: 'hsl(0, 0%, 27%)',
              borderColor: 'divider',
              color: 'hsl(0, 0%, 90%)',
              transition: 'margin-left 0.3s ease-in-out',
            },
            textTransform: 'none',
          }}
          variant="contained"
        >
          Tilføj Node
        </Button>
      </Box>
    </Stack>
  );
};

export default AddNodeTab;
